import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Mail, Send, Settings, FileText, Upload, Layout } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { TemplateManager } from './TemplateManager';
import { CampaignScheduler } from './CampaignScheduler';
import { EmailAnalytics } from './EmailAnalytics';

interface Lead {
  id: string;
  name: string | null;
  email: string | null;
  company: string | null;
  phone: string | null;
  interest_type: string | null;
  message: string | null;
}

interface TemplateSettings {
  companyName: string;
  companyDescription: string;
  logo: string;
  primaryColor: string;
  signatureName: string;
  signatureTitle: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  autoAttachCatalog: boolean;
}

export const EmailTemplateSystem = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [selectedLead, setSelectedLead] = useState<string>('');
  const [subject, setSubject] = useState('Premium Smart Toilet Solutions from Aureum VII Trading');
  const [customMessage, setCustomMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [additionalFiles, setAdditionalFiles] = useState<File[]>([]);
  
  const [settings, setSettings] = useState<TemplateSettings>({
    companyName: 'Aureum VII Trading',
    companyDescription: 'Leading European distributor of premium smart toilet technology. We bring innovative bathroom solutions from top European manufacturers to discerning clients worldwide.',
    logo: '/lovable-uploads/aureum-vii-trading-logo.png',
    primaryColor: '#D4AF37',
    signatureName: 'Sales Team',
    signatureTitle: 'Smart Bathroom Solutions Specialist',
    phone: '+34 123 456 789',
    email: 'info@aureum7trading.com',
    website: 'www.aureum7trading.com',
    address: 'Marbella, Spain',
    autoAttachCatalog: true,
  });

  useEffect(() => {
    fetchLeads();
    loadSettings();
  }, []);

  const fetchLeads = async () => {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);

    if (!error && data) {
      setLeads(data);
    }
  };

  const loadSettings = () => {
    const saved = localStorage.getItem('emailTemplateSettings');
    if (saved) {
      setSettings(JSON.parse(saved));
    }
  };

  const saveSettings = () => {
    localStorage.setItem('emailTemplateSettings', JSON.stringify(settings));
    toast.success('Template settings saved');
  };

  const generateEmailHTML = (lead: Lead) => {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${settings.companyName}</title>
  <style>
    body { margin: 0; padding: 0; font-family: 'Arial', sans-serif; background-color: #f4f4f4; }
    .container { max-width: 600px; margin: 0 auto; background-color: #ffffff; }
    .header { background: linear-gradient(135deg, ${settings.primaryColor} 0%, #B8960C 100%); padding: 40px 20px; text-align: center; }
    .logo { max-width: 180px; height: auto; }
    .content { padding: 40px 30px; }
    .greeting { font-size: 24px; color: #333; margin-bottom: 20px; font-weight: bold; }
    .message { font-size: 16px; line-height: 1.6; color: #555; margin-bottom: 20px; }
    .cta-button { display: inline-block; background-color: ${settings.primaryColor}; color: #ffffff; padding: 15px 40px; text-decoration: none; border-radius: 5px; font-weight: bold; margin: 20px 0; }
    .products-section { background-color: #f9f9f9; padding: 30px; margin: 30px 0; border-radius: 8px; }
    .product-item { margin-bottom: 20px; }
    .product-name { font-weight: bold; color: ${settings.primaryColor}; font-size: 18px; }
    .footer { background-color: #1a1a1a; color: #ffffff; padding: 30px; text-align: center; }
    .signature { margin-top: 30px; padding-top: 20px; border-top: 2px solid #eee; }
    .contact-info { margin: 10px 0; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <img loading="lazy" decoding="async" src="${settings.logo}" alt="${settings.companyName} logo" class="logo">
    </div>
    
    <div class="content">
      <div class="greeting">Dear ${lead.name || 'Valued Customer'},</div>
      
      <div class="message">
        <p>Thank you for your interest in ${settings.companyName}. ${settings.companyDescription}</p>
        
        ${customMessage ? `<p>${customMessage}</p>` : ''}
        
        <p>We specialize in premium smart toilet solutions that combine European engineering excellence with cutting-edge technology. Our collection includes:</p>
      </div>
      
      <div class="products-section">
        <div class="product-item">
          <div class="product-name">🏆 LUXA Series - Flagship Collection</div>
          <p>The pinnacle of luxury with tankless instant heating, AI-powered features, and unlimited comfort.</p>
        </div>
        
        <div class="product-item">
          <div class="product-name">🌟 LUNA M9 - Space-Efficient Excellence</div>
          <p>Perfect for modern spaces with advanced wellness features and quiet operation.</p>
        </div>
        
        <div class="product-item">
          <div class="product-name">💎 AURELIUS Series - Superior Comfort</div>
          <p>Wider comfort design with automatic cleaning and premium certifications.</p>
        </div>
        
        <div class="product-item">
          <div class="product-name">🎨 NOVA Series - Architectural Elegance</div>
          <p>Minimalist design for high-end projects with smart home integration.</p>
        </div>
        
        <div class="product-item">
          <div class="product-name">🌿 VENA Series - Eco-Luxury</div>
          <p>Sustainable luxury with water-saving technology and premium features.</p>
        </div>
      </div>
      
      <center>
        <a href="https://${settings.website}" class="cta-button">Explore Our Collection</a>
      </center>
      
      <div class="message">
        <p>All our products come with:</p>
        <ul>
          <li>2-year comprehensive warranty</li>
          <li>Professional installation support</li>
          <li>European quality certifications</li>
          <li>Dedicated customer service</li>
        </ul>
        
        <p>Please find our detailed product catalog attached to this email.</p>
      </div>
      
      <div class="signature">
        <p><strong>${settings.signatureName}</strong><br>
        ${settings.signatureTitle}<br>
        ${settings.companyName}</p>
        
        <div class="contact-info">
          <p>📧 ${settings.email}<br>
          📞 ${settings.phone}<br>
          🌐 ${settings.website}<br>
          📍 ${settings.address}</p>
        </div>
      </div>
    </div>
    
    <div class="footer">
      <p>© ${new Date().getFullYear()} ${settings.companyName}. All rights reserved.</p>
      <p style="font-size: 12px; color: #999; margin-top: 10px;">
        This email was sent because you expressed interest in our premium smart toilet solutions.
      </p>
    </div>
  </div>
</body>
</html>
    `.trim();
  };

  const handleSendEmail = async () => {
    if (!selectedLead) {
      toast.error('Please select a lead');
      return;
    }

    const lead = leads.find(l => l.id === selectedLead);
    if (!lead || !lead.email) {
      toast.error('Lead email not found');
      return;
    }

    setIsSending(true);
    try {
      const emailHTML = generateEmailHTML(lead);

      const { data, error } = await supabase.functions.invoke('send-email-template', {
        body: {
          to: lead.email,
          subject,
          html: emailHTML,
          attachCatalog: settings.autoAttachCatalog,
          additionalFiles: additionalFiles.length > 0 ? await Promise.all(
            additionalFiles.map(async (file) => ({
              filename: file.name,
              content: await fileToBase64(file),
            }))
          ) : undefined,
        }
      });

      if (error) throw error;

      toast.success('Email sent successfully!');
      setSelectedLead('');
      setCustomMessage('');
      setAdditionalFiles([]);
    } catch (error: any) {
      console.error('Error sending email:', error);
      toast.error(error.message || 'Failed to send email');
    } finally {
      setIsSending(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAdditionalFiles(Array.from(e.target.files));
      toast.success(`${e.target.files.length} file(s) selected`);
    }
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="compose" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="compose">
            <Mail className="w-4 h-4 mr-2" />
            Compose
          </TabsTrigger>
          <TabsTrigger value="templates">
            <Layout className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="schedule">
            <FileText className="w-4 h-4 mr-2" />
            Schedule
          </TabsTrigger>
          <TabsTrigger value="preview">
            <FileText className="w-4 h-4 mr-2" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <Mail className="w-4 h-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Send Branded Email</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="lead">Select Lead</Label>
                <Select value={selectedLead} onValueChange={setSelectedLead}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a lead..." />
                  </SelectTrigger>
                  <SelectContent>
                    {leads.map((lead) => (
                      <SelectItem key={lead.id} value={lead.id}>
                        {lead.name || lead.email} - {lead.company || 'No company'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="subject">Email Subject</Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="message">Custom Message (Optional)</Label>
                <Textarea
                  id="message"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  rows={4}
                  placeholder="Add a personalized message here..."
                />
              </div>

              <div>
                <Label htmlFor="files">Additional Attachments (Optional)</Label>
                <Input
                  id="files"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileUpload}
                />
                {additionalFiles.length > 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {additionalFiles.length} file(s) selected
                  </p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="auto-catalog"
                  checked={settings.autoAttachCatalog}
                  onCheckedChange={(checked) => 
                    setSettings({ ...settings, autoAttachCatalog: checked })
                  }
                />
                <Label htmlFor="auto-catalog">Auto-attach product catalog PDF</Label>
              </div>

              <Button
                onClick={handleSendEmail}
                disabled={isSending || !selectedLead}
                className="w-full"
                size="lg"
              >
                <Send className="w-4 h-4 mr-2" />
                {isSending ? 'Sending...' : 'Send Email'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle>Email Template Library</CardTitle>
            </CardHeader>
            <CardContent>
              <TemplateManager />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="schedule">
          <Card>
            <CardHeader>
              <CardTitle>Campaign Scheduler</CardTitle>
            </CardHeader>
            <CardContent>
              <CampaignScheduler />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview">
          <Card>
            <CardHeader>
              <CardTitle>Email Preview</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedLead ? (
                <div 
                  className="border rounded-lg p-4 bg-white"
                  dangerouslySetInnerHTML={{ 
                    __html: generateEmailHTML(leads.find(l => l.id === selectedLead)!) 
                  }}
                />
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  Select a lead to preview the email
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics">
          <Card>
            <CardHeader>
              <CardTitle>Email Analytics</CardTitle>
            </CardHeader>
            <CardContent>
              <EmailAnalytics />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Template Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  value={settings.companyName}
                  onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="companyDescription">Company Description</Label>
                <Textarea
                  id="companyDescription"
                  value={settings.companyDescription}
                  onChange={(e) => setSettings({ ...settings, companyDescription: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="logo">Logo URL</Label>
                <Input
                  id="logo"
                  value={settings.logo}
                  onChange={(e) => setSettings({ ...settings, logo: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="primaryColor">Primary Brand Color (Hex)</Label>
                <Input
                  id="primaryColor"
                  type="color"
                  value={settings.primaryColor}
                  onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="signatureName">Signature Name</Label>
                <Input
                  id="signatureName"
                  value={settings.signatureName}
                  onChange={(e) => setSettings({ ...settings, signatureName: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="signatureTitle">Signature Title</Label>
                <Input
                  id="signatureTitle"
                  value={settings.signatureTitle}
                  onChange={(e) => setSettings({ ...settings, signatureTitle: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={settings.phone}
                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={settings.email}
                    onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={settings.website}
                  onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={settings.address}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                />
              </div>

              <Button onClick={saveSettings} className="w-full">
                Save Template Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
